import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus } from "lucide-react";
import { useState } from "react";
import type { BillingApiKeyRecord } from "@hostdock/types";
import {
  Badge, Button, ConfirmDialog, DataTable, Dialog, EmptyState, Field, FormError, formatDateTime, formatRelative, Input, Notice, OneTimeSecret, PageHeader, Panel, toast,
} from "@hostdock/ui";
import { api } from "../api";
import { RequirePermission } from "../lib/guard";

export function BillingPage() {
  return (
    <RequirePermission anyOf={["BILLING_MANAGE"]}>
      <Billing />
    </RequirePermission>
  );
}

function Billing() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["billing-keys"], queryFn: api.billing.keys });
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<BillingApiKeyRecord | null>(null);
  const revoke = useMutation({ meta: { inlineError: true },
    mutationFn: (k: BillingApiKeyRecord) => api.billing.revokeKey(k.id),
    onSuccess: () => { toast.success("Key revoked"); setRevoking(null); void qc.invalidateQueries({ queryKey: ["billing-keys"] }); },
  });

  return (
    <>
      <PageHeader
        title="Billing integration"
        description="WHMCS provisions, suspends, unsuspends, terminates and re-plans hosting packages through HostDock's provisioning API. HostDock does not bill."
        actions={<Button variant="primary" icon={<Plus size={14} />} onClick={() => setCreating(true)}>New API key</Button>}
      />
      <Panel title="API keys" flush>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(k) => k.id}
          empty={<EmptyState icon={KeyRound} title="No billing API keys" description="Create one and paste it into the WHMCS server's Access Hash." />}
          columns={[
            { key: "n", header: "Name", cell: (k) => <div className="min-w-0"><p className="truncate font-medium">{k.name}</p><p className="font-mono text-[11.5px] text-muted">{k.prefix}…</p></div> },
            { key: "s", header: "Status", cell: (k) => (k.revokedAt ? <Badge tone="neutral">Revoked</Badge> : <Badge tone="success">Active</Badge>) },
            { key: "u", header: "Last used", cell: (k) => <span className="text-muted" title={k.lastUsedAt ? formatDateTime(k.lastUsedAt) : undefined}>{k.lastUsedAt ? `${formatRelative(k.lastUsedAt)}${k.lastUsedIp ? ` · ${k.lastUsedIp}` : ""}` : "Never"}</span> },
            { key: "c", header: "Created", cell: (k) => <span className="text-muted">{formatDateTime(k.createdAt)}</span>, hideOnMobile: true },
            { key: "a", header: "", className: "w-24 text-right", cell: (k) => !k.revokedAt && <Button size="sm" variant="ghost" className="text-danger" onClick={() => setRevoking(k)}>Revoke</Button> },
          ]}
        />
      </Panel>

      <Panel title="Connect WHMCS" className="mt-5">
        <ol className="list-decimal space-y-2 pl-5 text-[13px]">
          <li>Copy <code className="font-mono">integrations/whmcs/modules/servers/hostdock</code> from the HostDock repository into WHMCS's <code className="font-mono">modules/servers/</code>.</li>
          <li>In WHMCS, add a server: Module <b>HostDock</b>, Hostname = this HostDock API host, <b>Secure</b> on (HTTPS is required and certificates are always verified), Access Hash = a key from this page. Use <b>Test Connection</b>.</li>
          <li>On each product, choose Module <b>HostDock</b> and pick the HostDock plan; optionally set a region code.</li>
        </ol>
        <div className="mt-4"><Notice tone="info">
          Every call is idempotent on the WHMCS service ID, so retries never create a second package. <b>Terminate</b> stops the package from serving but keeps its files and databases; delete data in HostDock deliberately. A WHMCS client whose e-mail already belongs to an unlinked HostDock customer is refused until you link them on the customer's page.
        </Notice></div>
      </Panel>

      <CreateKeyDialog open={creating} onOpenChange={setCreating} onDone={() => void qc.invalidateQueries({ queryKey: ["billing-keys"] })} />
      <ConfirmDialog
        open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)} title={`Revoke “${revoking?.name}”?`} confirmLabel="Revoke key"
        description="WHMCS stops being able to provision or change hosting accounts with this key immediately. This cannot be undone."
        loading={revoke.isPending} error={revoke.error} onConfirm={() => revoking && revoke.mutate(revoking)}
      />
    </>
  );
}

function CreateKeyDialog({ open, onOpenChange, onDone }: { open: boolean; onOpenChange(o: boolean): void; onDone(): void }) {
  const [name, setName] = useState("WHMCS");
  const [secret, setSecret] = useState<string | null>(null);
  const create = useMutation({ meta: { inlineError: true }, mutationFn: () => api.billing.createKey(name.trim()), onSuccess: (r) => { setSecret(r.key); onDone(); } });
  const close = (o: boolean) => { if (!o) { setSecret(null); setName("WHMCS"); create.reset(); } onOpenChange(o); };
  return (
    <Dialog open={open} onOpenChange={close} title={secret ? "Your new billing API key" : "New billing API key"} size="sm"
      footer={secret ? <Button variant="primary" onClick={() => close(false)}>Done</Button> : <><Button variant="ghost" onClick={() => close(false)}>Cancel</Button><Button variant="primary" disabled={!name.trim()} loading={create.isPending} onClick={() => create.mutate()}>Create key</Button></>}>
      {secret ? <OneTimeSecret label="Key" value={secret} note="Copy it now into the WHMCS server's Access Hash. HostDock keeps only a one-way hash and cannot show it again." /> : (
        <div className="space-y-4">
          <Field label="Name" hint="For your reference, e.g. the WHMCS instance.">{(p) => <Input {...p} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />}</Field>
          <FormError error={create.error} />
        </div>
      )}
    </Dialog>
  );
}
